import React, { useState, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';

import { Select, Skeleton, Form } from 'antd';

import { renderNoDataOrSpinner } from '../../utils';

import './styles.less';
import { isArray, isEmpty } from 'lodash';

const PAGE_SIZE = 15;

const SelectWithFetchNext = ({
  rootContainer,
  onSelect,
  dropdownMatchSelectWidth,
  keyName,
  titleName,
  textName,
  fetchNext,
  placeholder,
  createNewItemIfSearchNotExist,
  fetchImmediately,
  onEnterPress,
  multipleMode,
  customRender,
  disabled,
  subscribeDataChangingAndFetchNewData,
  formItemName,
  rules,
  putInFormItem,
  className,
  enableSelectAll,
  onSelectAll,
  hideFormLabel,
  initialData,
}) => {
  const dispatch = useDispatch();

  const [dataSource, setDataSource] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [fieldTimeout, setFieldTimeout] = useState();
  const [searchFieldValue, setSearchFieldValue] = useState('');
  const [pageNumber, setPageNumber] = useState(1);
  const [newData, setNewData] = useState([]);
  const [value, setValue] = useState();
  const [isDropdownVisibleDirty, setIsDropdownVisibleDirty] = useState(false);
  const [selectAllLoading, setSelectAllLoading] = useState(false);

  useEffect(() => {
    if (isArray(initialData) && !isEmpty(initialData) && newData.length < 1 && fetchImmediately === true) {
      setNewData(initialData);
    }
  }, [initialData]);

  useEffect(() => {
    if (fetchImmediately === true) {
      setLoading(true);

      dispatch(fetchNext({ pageNumber, pageSize: PAGE_SIZE })).then((result) => {
        setNewData(() => {
          setDataSource(result.dataList);
          setHasMore(result.hasMore);
          setLoading(false);

          return [];
        });
      });

      setIsDropdownVisibleDirty(true);
    }
  }, []);

  useEffect(() => {
    if (dataSource) {
      let newValue = dataSource?.slice() ?? [];

      newValue = [...newData, ...newValue];

      setNewData(newValue);
    }
  }, [dataSource]);

  useEffect(() => {
    if (isDropdownVisibleDirty) {
      handleSearch(searchFieldValue);
    }
  }, [subscribeDataChangingAndFetchNewData]);

  const onSelectScroll = (event) => {
    const target = event.currentTarget;
    const maxScroll = target.scrollHeight - target.clientHeight;
    const atTheEnd = target.scrollTop + 50 >= maxScroll;

    if (atTheEnd && hasMore === true && !loading) {
      loadMoreData();
    }
  };

  const mappedData = () => {
    const result = [];

    if (multipleMode && enableSelectAll) {
      result.push(
        <Select.Option key="select-all-key" value="select-all" disabled={selectAllLoading} className="select-all-key-class">
          <div
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleSelectAll();
            }}
            style={{ width: '100%', padding: '5px 12px' }}
          >
            {selectAllLoading ? <Skeleton paragraph={{ rows: 1 }} active /> : 'Select All'}
          </div>
        </Select.Option>,
      );
    }

    if (!selectAllLoading) {
      if (searchFieldValue && createNewItemIfSearchNotExist) {
        result.push(
          <Select.Option key={'newChatNumber'} value={searchFieldValue} title={searchFieldValue} item_object={{ value: searchFieldValue }}>
            {searchFieldValue}
          </Select.Option>,
        );
      }

      if (newData && newData.length > 0) {
        result.push(
          newData.map((d, index) => (
            <Select.Option key={d[keyName ?? 'key'] + index} value={d[keyName ?? 'key']} title={d[titleName ?? 'value']} item_object={d}>
              {d[textName ?? 'value']}
            </Select.Option>
          )),
        );
      }

      if (loading) {
        result.push(
          <Select.Option key={'loader-key'} disabled={true}>
            <Skeleton paragraph={{ rows: 1 }} active />
          </Select.Option>,
        );
      }
    }

    return result;
  };

  const handleSearch = (searchString) => {
    setPageNumber(1);

    if (fieldTimeout) {
      clearTimeout(fieldTimeout);
      setFieldTimeout(undefined);
    }

    setSearchFieldValue(searchString);

    const timeout = setTimeout(() => {
      setLoading(true);

      dispatch(fetchNext({ pageNumber: 1, pageSize: PAGE_SIZE, searchString })).then((result) => {
        setNewData([]);
        setDataSource(result.dataList);
        setHasMore(result.hasMore);
        setLoading(false);
      });
    }, 500);

    setFieldTimeout(timeout);
  };

  const handleSelectAll = async () => {
    setSelectAllLoading(true);
    try {
      const result = await dispatch(fetchNext({ pageSize: 500, pageNumber: 1, searchString: undefined }));

      const allItems = result.dataList;

      setNewData(allItems);
      setValue(allItems.map((item) => item[keyName ?? 'key']));
      setHasMore(false);

      if (typeof onSelectAll === 'function') {
        onSelectAll(allItems, formItemName);
      }

      if (typeof onSelect === 'function') {
        onSelect(allItems);
      }
    } finally {
      setSelectAllLoading(false);
    }
  };

  const loadMoreData = () => {
    const newPageNumber = pageNumber + 1;

    setLoading(true);

    dispatch(fetchNext({ pageSize: PAGE_SIZE, pageNumber: newPageNumber, searchString: searchFieldValue })).then((result) => {
      setDataSource(result.dataList);
      setHasMore(result.hasMore);
      setLoading(false);
    });

    setPageNumber(newPageNumber);
  };

  const onChange = (value, option) => {
    setValue(value);

    if (typeof onSelect === 'function') {
      if (multipleMode) {
        onSelect(option.map((x) => x.item_object));
      } else {
        onSelect(option.item_object);
      }
    }
  };

  const onDropdownVisibleChange = () => {
    if (!isDropdownVisibleDirty) {
      setLoading(true);

      dispatch(fetchNext({ pageNumber, pageSize: PAGE_SIZE })).then((result) => {
        setDataSource(result.dataList);
        setHasMore(result.hasMore);
        setLoading(false);
      });

      setIsDropdownVisibleDirty(true);
    }
  };

  const onInputKeyDown = (event) => {
    const key = event.key || event.keyCode;

    if (key === 'Enter' || key === 13) {
      onEnterPress();
    }
  };

  const getMaxTagPlaceholder = (omittedValues) => {
    if (omittedValues && omittedValues.length > 0) {
      return `+${omittedValues.length} Items`;
    }
  };

  let selector = (
    <Select
      allowClear
      showSearch
      showArrow
      getPopupContainer={rootContainer}
      onChange={onChange}
      loading={loading}
      dropdownMatchSelectWidth={dropdownMatchSelectWidth}
      onPopupScroll={onSelectScroll}
      notFoundContent={renderNoDataOrSpinner(loading)}
      autoClearSearchValue={false}
      placeholder={searchFieldValue ? searchFieldValue : placeholder}
      searchValue={searchFieldValue}
      onSearch={handleSearch}
      filterOption={false}
      onDropdownVisibleChange={onDropdownVisibleChange}
      onInputKeyDown={onInputKeyDown}
      mode={multipleMode ? 'multiple' : undefined}
      disabled={disabled}
      maxTagCount={1}
      maxTagPlaceholder={getMaxTagPlaceholder}
      className={className}
      value={value}
    >
      {typeof customRender === 'function' ? (
        loading ? (
          <Select.Option key={'loader-key'} disabled={true}>
            <Skeleton paragraph={{ rows: 1 }} active />
          </Select.Option>
        ) : (
          customRender(newData)
        )
      ) : (
        mappedData()
      )}
    </Select>
  );

  if (putInFormItem) {
    selector = (
      <Form.Item name={[formItemName]} label={hideFormLabel ? null : placeholder} className="filter-form-item" rules={rules}>
        {selector}
      </Form.Item>
    );
  }

  return selector;
};

export default SelectWithFetchNext;
